import { ChangeDetectionStrategy, Component, ElementRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IControl } from 'core/dynamic-form';
import { LoaderService, ScreenUtilsService, SnackbarService } from 'core/services';

import { HttpErrorResponse } from '@angular/common/http';
import { UserRemoteControllerService } from 'core/api';
import { ConfigService } from 'core/config';
import { EProcessStatus } from 'core/dto';
import { take } from 'rxjs';
import { designerPaths } from '../../designer.paths';
import { JwtService } from '../../global-services/jwt.service';
import { DsgDeepTranslationKey } from '../../pipes/translate.pipe';
import { PageBaseDirective } from '../../shared-components/page-base.directive';



@Component({
  selector: 'ss-auth',
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.scss'],
  host: {
    class: 'padding-standard full-stretch flex-column flex--align-center'
  },
  hostDirectives: [PageBaseDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class AuthComponent {

  readonly controls: IControl[] = [
    { dataField: 'email', type: 'email', label: 'ss-auth.email', validationRules: [{ type: 'email' }, { type: 'required' }] },
    { dataField: 'password', type: 'password', label: 'ss-auth.password', validationRules: [{ type: 'required' }] }
  ]

  readonly designerLogin = this._configService.appConfig.designerLogin;
  private _accessedRoutePath: string

  readonly isSmallerScreenObs = this._screenUtilsService.isSmallerScreenObs;

  @ViewChild('cardHeader', { read: ElementRef<HTMLElement> })
  private _header: ElementRef<HTMLElement>
  get header() {
    return this._header?.nativeElement
  }


  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _userRemoteControllerService: UserRemoteControllerService,
    private readonly _loaderService: LoaderService<DsgDeepTranslationKey>,
    private readonly _jwtService: JwtService,
    private readonly _router: Router,
    private readonly _configService: ConfigService,
    private readonly _snackbarService: SnackbarService<DsgDeepTranslationKey>,
    private readonly _screenUtilsService: ScreenUtilsService
  ) {


    const { accessedRoutePath } = this._activatedRoute.snapshot.queryParams;
    this._accessedRoutePath = accessedRoutePath;

    if (this.designerLogin === 'oidc') {

      const id = this._loaderService.show({ text: 'ss-auth.redirecting' })

      this._userRemoteControllerService
        .oidcLogin({ accessedRoutePath: this._accessedRoutePath })
        .pipe(take(1), this._loaderService.disposeLoader(id))
        .subscribe(({ redirectUri }) => {
          window.location.href = redirectUri;
        })

    }

    if (this.designerLogin === 'sso') {

      const id = this._loaderService.show({ text: 'ss-auth.signing-in' })
      this._userRemoteControllerService
        .sso()
        .pipe(take(1), this._loaderService.disposeLoader(id))
        .subscribe(({ jwtToken }) => {
          this._jwtService.refreshTokenExistsSubject.next(true);
          this._jwtService.jwtSubject.next(jwtToken);

          this.navigateFromLogin();
        })
    }

  }


  formSubmit(formData: any) {

    const id = this._loaderService.show({ text: 'ss-auth.signing-in' })

    this._userRemoteControllerService
      .emailAuth({
        email: formData.email,
        password: formData.password
      })
      .pipe(
        this._loaderService.disposeLoader(id),
        take(1),
        this._snackbarService.handleError({
          getErrorMessage: (errorResponse: HttpErrorResponse) => errorResponse.error.error?.status.message,
          type: 'error',
        })
      )
      .subscribe(({ jwtToken }) => {

        this._jwtService.jwtSubject.next(jwtToken);
        this._jwtService.refreshTokenExistsSubject.next(true);
        this.navigateFromLogin();
      })

  }


  navigateFromLogin() {

    if (!this._accessedRoutePath) {

      this._router.navigate([designerPaths.processOverview, EProcessStatus.WaitingForMe])
      return;
    }

    const url = this._router.parseUrl(this._accessedRoutePath);
    this._accessedRoutePath = undefined;
    this._router.navigateByUrl(url);

  }
}
